import { ActivityStatus } from 'connect-graphql/generated';

import {
  AssignmentLateOutlined,
  AssignmentOutlined,
  AssignmentReturnedOutlined,
  AssignmentReturnOutlined,
  AssignmentTurnedInOutlined,
  type SvgIconComponent,
} from '@mui/icons-material';

export const ActivityStatusModels = [
  {
    alternateLabel: 'Unsubmitted',
    bgcolor: 'alert.infoBgColor',
    color: 'alert.infoTextColor',
    icon: AssignmentOutlined,
    label: 'Unsubmitted',
    overdueColor: 'error.dark',
    overdueIcon: AssignmentLateOutlined,
    status: ActivityStatus.Unsubmitted,
  },
  {
    alternateLabel: 'Returned',
    bgcolor: 'alert.warningBgColor',
    color: 'alert.warningTextColor',
    icon: AssignmentReturnOutlined,
    label: 'Returned',
    overdueColor: 'warning.dark',
    overdueIcon: AssignmentReturnOutlined,
    status: ActivityStatus.Returned,
  },
  {
    alternateLabel: 'Pending Approval',
    bgcolor: 'alert.successBgColor',
    color: 'alert.successTextColor',
    icon: AssignmentTurnedInOutlined,
    label: 'Approved',
    overdueColor: 'success.dark',
    overdueIcon: AssignmentTurnedInOutlined,
    status: ActivityStatus.Approved,
  },
  {
    alternateLabel: 'Submitted',
    bgcolor: 'alert.pendingBgColor',
    color: 'alert.pendingTextColor',
    icon: AssignmentReturnedOutlined,
    label: 'Submitted',
    overdueColor: 'pending.main',
    overdueIcon: AssignmentReturnedOutlined,
    status: ActivityStatus.Submitted,
  },
] as const satisfies {
  readonly alternateLabel: string;
  readonly bgcolor: string;
  readonly color: string;
  readonly icon: SvgIconComponent;
  readonly label: string;
  readonly overdueColor: string;
  readonly overdueIcon: SvgIconComponent;
  readonly status: ActivityStatus;
}[];

export type ActivityStatusModel<S extends ActivityStatus = ActivityStatus> = Extract<
  (typeof ActivityStatusModels)[number],
  { status: `${S}` }
>;

export const getActivityStatusModel = <S extends ActivityStatus>(
  status: S,
): ActivityStatusModel<S> => {
  const model = ActivityStatusModels.find(({ status: s }) => s === status);
  if (!model) {
    throw new Error(`Could not find activity status model for status ${status}!`);
  }
  return model as ActivityStatusModel<S>;
};

type ActivityStatusLabel<
  S extends ActivityStatus = ActivityStatus,
  I extends boolean = boolean,
> = I extends true ? 'Overdue' | ActivityStatusModel<S>['label'] : ActivityStatusModel<S>['label'];

/**
 * Returns the label, {@link ActivityStatusLabel}, associated with the activity status,
 * {@link ActivityStatus}.
 *
 * @param {ActivityStatus} activityStatus
 * @param {boolean} isOverdue
 *   Reports if the current date is after the due date and the activity is unsubmitted.
 *
 * @returns {ActivityStatusLabel<S, I>}
 *   The label associated with the activity status, {@link ActivityStatus}, and overdue state.
 */
export const getActivityStatusLabel = <S extends ActivityStatus, I extends boolean>(
  activityStatus: S,
  isOverdue: I,
): ActivityStatusLabel<S, I> => {
  if (isOverdue) {
    return 'Overdue' as ActivityStatusLabel<S, I>;
  }
  return getActivityStatusModel(activityStatus).label as ActivityStatusLabel<S, I>;
};

type ActivityStatusIcon<
  S extends ActivityStatus = ActivityStatus,
  I extends boolean = boolean,
> = I extends true ? ActivityStatusModel<S>['overdueIcon'] : ActivityStatusModel<S>['icon'];

/**
 * Returns the icon, {@link ActivityStatusIcon}, associated with the activity status,
 * {@link ActivityStatus}.
 *
 * @param {ActivityStatus} activityStatus
 * @param {boolean} isOverdue
 *   Reports if the current date is after the due date and the activity is unsubmitted.
 *
 * @returns {ActivityStatusIcon<S, I>}
 *   The icon associated with the activity status, {@link ActivityStatus}, and overdue state.
 */
export const getActivityStatusIcon = <S extends ActivityStatus, I extends boolean>(
  activityStatus: S,
  isOverdue: I,
): ActivityStatusIcon<S, I> =>
  isOverdue
    ? getActivityStatusModel(activityStatus).overdueIcon
    : getActivityStatusModel(activityStatus).icon;

type ActivityStatusColor<
  S extends ActivityStatus = ActivityStatus,
  I extends boolean = boolean,
> = I extends true ? ActivityStatusModel<S>['overdueColor'] : ActivityStatusModel<S>['color'];

/**
 * Returns the color, {@link ActivityStatusColor}, associated with the activity status,
 * {@link ActivityStatus}.
 *
 * @param {ActivityStatus} activityStatus
 * @param {boolean} isOverdue
 *   Reports if the current date is after the due date and the activity is unsubmitted.
 *
 * @returns {ActivityStatusColor<S, I>}
 *   The color associated with the activity status, {@link ActivityStatus}, and overdue state.
 */
export const getActivityStatusColor = <S extends ActivityStatus, I extends boolean>(
  activityStatus: S,
  isOverdue: I,
): ActivityStatusColor<S, I> =>
  isOverdue
    ? (getActivityStatusModel(activityStatus).overdueColor as ActivityStatusColor<S, I>)
    : (getActivityStatusModel(activityStatus).color as ActivityStatusColor<S, I>);

export const getActivityStatusBackgroundStatusColor = (
  activityStatus: ActivityStatus,
  isOverdue: boolean,
) => {
  if (isOverdue) {
    return 'error.light' as const;
  }
  return getActivityStatusModel(activityStatus).bgcolor;
};
